import { useEffect, useRef } from 'react';
import { Container, Heading, Reveal, Section, Text, buttonClasses } from '@nexa/ui';
import heroBg1200 from '@/assets/hero-bg-1200.webp';
import heroBg1536 from '@/assets/hero-bg-1536.webp';
import heroBg768 from '@/assets/hero-bg-768.webp';
import { useContactDialog } from '@/core/contact';
import { useCopy } from '@/core/i18n';

/**
 * `#cta` — `template.html:1041`. Section DUY NHẤT dùng `--spacing-section-lg` (104px) ⇒ `size="lg"`.
 *
 * Ảnh nền dùng LẠI asset của hero: thiết kế trỏ cùng một file ở cả hai chỗ (`:606` và `:1042`;
 * grep ra đúng 2 lượt), nên không cần sinh ảnh mới. Khác hero ở hai điểm — opacity riêng
 * (`--opacity-cta-image`, thấp hơn hero) và `loading="lazy"` + không `fetchPriority`, vì section
 * này ở CUỐI trang nên nó không phải ứng viên LCP.
 *
 * BÓNG NÚT TRẮNG đi qua THAM SỐ THỨ BA của `buttonClasses`, không qua variant. Đây là điểm chính
 * của cả task: `--shadow-btn-light` khai trong theme mà 0 hộ dùng, y hệt bản `-hero` từng làm nút
 * CTA của hero MẤT bóng nhoè 40px trên nền navy — reviewer bắt được, không cổng nào bắt, vì một
 * token khai mà không dùng thì không đỏ ở đâu.
 *
 * An toàn khi truyền thêm vì variant `inverse` KHÔNG mang shadow nào (đã grep): không có utility
 * shadow nào tranh chỗ. Nếu dùng `primary` rồi truyền bóng thì HAI class shadow cùng tồn tại và
 * `cn()` không phải tailwind-merge ⇒ không kiểm soát được cái nào thắng.
 *
 * Nút phụ dùng biến thể RIÊNG `on-dark-cta`, không phải `on-dark`: thiết kế cho viền nút này mức
 * 45% còn nút phụ của hero là 42%. Nợ D-3 của lô D đã trả ở vé #10 bằng cách thêm biến thể — KHÔNG
 * bằng cách truyền class viền qua tham số ba, vì hai utility cùng thuộc tính sẽ đấu nhau theo thứ
 * tự Tailwind xếp trong CSS chứ không theo thứ tự ta viết.
 *
 * Hai nút phụ lệch nhau ở HAI thuộc tính, và bảng nợ của lô D chỉ khai một: hero (thiết kế dòng
 * 617) viền 42% + hover nền 12%; nút này (dòng 1048) viền 45% + hover nền 14%. Nợ viền trả ở vé
 * #10, nợ hover ở vé #38 — hai vé vì user quyết riêng từng giá trị alpha.
 *
 * Hai `href="#cta"` tự trỏ vào section chứa chúng: placeholder CÓ CHỦ Ý cho tới khi có trang đích
 * (user chốt). Thiết kế gốc để hành vi thật trong `sc-camel-on-click`, thứ không chuyển sang HTML
 * được. Đừng đổi thành `#` và đừng bỏ `href` — bỏ là mất vai link và mất focus bàn phím.
 */
export function CtaSection() {
  const copy = useCopy();
  const openContact = useContactDialog();
  /*
   * Ref đặt trên `<span className="nx-halo">` chứ không trên `Section`: `Section` KHÔNG forward
   * `ref` (nó nhận một tập prop đóng, không có `ref`), và `packages/ui/src/components/Section.tsx`
   * nằm ngoài phạm vi sửa của task này. Từ span suy ra hai thứ, mỗi thứ vì một lý do khác nhau:
   *   - `parentElement` là phần tử NHẬN `mousemove`. Không gắn được lên chính span vì span mang
   *     `pointer-events: none` ⇒ nó không bao giờ là target và event không đi qua nó.
   *   - `getBoundingClientRect()` lấy trên SPAN, không trên parent: phần trăm trong
   *     `radial-gradient` phân giải theo hộp vẽ của CHÍNH span, nên đo span là đo đúng hệ toạ độ
   *     mà giá trị sẽ được dùng. Đo parent chỉ tình cờ bằng nhau khi parent không có border.
   */
  const haloRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const halo = haloRef.current;
    const host = halo?.parentElement ?? null;
    if (halo === null || host === null) return;

    /*
     * Thiết bị KHÔNG có con trỏ thật thì thoát ngay, không gắn listener nào: ở đó `mousemove` chỉ
     * đến từ chuỗi tổng hợp sau `touchend`, tức một lần bắn duy nhất ở nơi ngón tay vừa rời —
     * quầng sáng sẽ ĐỨNG YÊN lệch một chỗ ngẫu nhiên thay vì bám gì cả, mà vẫn tốn pin. Không có
     * listener ⇒ hai biến giữ mặc định `50%` ⇒ quầng sáng nằm giữa khối, đúng như motion.css khai.
     */
    /*
     * `typeof matchMedia !== 'function'`, KHÔNG phải `window.matchMedia(...)` trực tiếp. Đây là
     * quy ước ĐANG CHẠY của repo, có ở bốn hộ (`useReveal.ts:64`, `useRotator.ts:56`,
     * `Header.tsx:98` và `:119`) và được hai ca test khai là kịch bản ĐƯỢC HỖ TRỢ
     * (`Header.test.tsx:311`, `useRotator.test.tsx:301`).
     *
     * Gọi không guard thì trình duyệt không có `matchMedia` sẽ NÉM, và vì đây là thân effect của
     * một section nên hệ quả không phải "mất hiệu ứng" mà là **mất cả section** qua error boundary.
     * Bốn hộ kia chỉ mất hiệu ứng. Đừng để hộ này là hộ duy nhất hỏng nặng hơn.
     */
    if (typeof matchMedia !== 'function') return;
    if (!matchMedia('(hover: hover)').matches) return;

    /*
     * Người bật GIẢM CHUYỂN ĐỘNG thì không gắn listener nào.
     *
     * Vì sao guard này KHÔNG dư dù `.nx-halo` đã có nhánh `transition: none` dưới reduce: nhánh
     * đó chỉ tắt phần *mờ dần của opacity*. Chuyển động THẬT của hiệu ứng là hai biến
     * `--nx-halo-x/y` do JS ghi MỖI FRAME theo con trỏ — một quầng 18rem bám con trỏ 1:1. Không
     * dòng CSS nào tắt được nó, và không cổng CSS nào THẤY được nó: `motionCss.test.ts` đọc
     * `motion.css` như văn bản, nên nó thấy `.nx-halo` khai `transition`, thấy nhánh reduce có
     * mặt, và báo xanh — trong khi thứ chuyển động nhất của cả lô vẫn chạy.
     *
     * Tệ hơn: dưới reduce thì `transition` đã tắt, nên quầng hiện TỨC THÌ thay vì mờ dần — tức
     * nhánh reduce làm hiệu ứng GIẬT HƠN chứ không dịu hơn.
     *
     * Hai chuyển động do JS lái còn lại trong repo đều hỏi reduce (`useReveal.ts:63`,
     * `useRotator.ts:55`). Đây là hộ thứ ba; đừng để nó là hộ duy nhất không hỏi.
     */
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /*
     * `mousemove` bắn nhiều lần trong một frame; ghi biến CSS ở mỗi event là ghi bỏ vì chỉ giá trị
     * cuối trước lần paint kế được dùng. Gộp bằng rAF: mỗi frame ghi đúng một lần, và `frame !== 0`
     * là cái chặn xếp hàng nhiều callback.
     */
    let frame = 0;
    const onMove = (event: MouseEvent) => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const rect = halo.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        halo.style.setProperty('--nx-halo-x', `${String(x)}%`);
        halo.style.setProperty('--nx-halo-y', `${String(y)}%`);
      });
    };

    host.addEventListener('mousemove', onMove);
    return () => {
      // Dọn CẢ HAI: listener, và frame đang chờ. Bỏ `cancelAnimationFrame` thì callback vẫn chạy
      // sau unmount và ghi style lên một phần tử đã rời khỏi cây — im lặng, nhưng là leak thật.
      host.removeEventListener('mousemove', onMove);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <Section
      id="cta"
      tone="gradient-cta"
      size="lg"
      labelledBy="cta-head"
      className="nx-halo-host relative overflow-hidden"
    >
      <img
        src={heroBg1200}
        srcSet={`${heroBg768} 768w, ${heroBg1200} 1200w, ${heroBg1536} 1536w`}
        sizes="100vw"
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 size-full object-cover opacity-[var(--opacity-cta-image)] mix-blend-screen"
      />

      <span ref={haloRef} className="nx-halo" />

      <Container width="narrow" className="relative text-center">
        <Reveal className="nx-rv-in">
          <Heading level={2} size="display" tone="on-dark" id="cta-head">
            {copy.cta.heading}
          </Heading>
        </Reveal>
        <Reveal>
          <Text size="lead" tone="on-dark-80" className="mx-auto mt-[18px] max-w-[56ch]">
            {copy.cta.lead}
          </Text>
        </Reveal>
        <Reveal className="mt-8 flex flex-wrap justify-center gap-3.5">
          <button
            type="button"
            onClick={openContact}
            className={buttonClasses('inverse', 'lg', 'shadow-btn-light')}
          >
            {copy.cta.primary}
          </button>
          <button
            type="button"
            onClick={openContact}
            className={buttonClasses('on-dark-cta', 'lg')}
          >
            {copy.cta.secondary}
          </button>
        </Reveal>
        {/*
          `-mr-[0.62em]` bù lỗi có thật của thiết kế: tracking cộng cả vào SAU ký tự cuối, nên một
          chuỗi canh giữa bị đẩy lệch phải ~8px. Lỗi của bản gốc, nhưng ta canh giữa nên ta thấy nó.
        */}
        <Reveal>
          <p className="mt-11 -mr-[0.62em] font-wordmark text-body-sm font-medium tracking-widest text-[var(--on-dark-text-55)] uppercase">
            {copy.cta.wordmark}
          </p>
        </Reveal>
      </Container>
    </Section>
  );
}
